import {
  Configuration,
  GetRapidGameDrawingDetailsRequest,
  KenoResultLocation,
  RapidDrawingResponse,
  RapidGamesApi,
} from '@interaction-gaming/game-data-service-browser-client'
import { UnscoredDrawResult } from '@API/game-results/types'
import {
  ExtraDrawsAddOnResult,
  MiniGameAddOnResult,
  MultiplierAddOnResult,
} from '@Config/draw-results-types'
import { Utils } from '@Util/utils'
import {
  GameDataConfig,
  getKenoConfigurationForDrawNumber,
} from '@API/gambyt-data-services/keno-api.ts'
import { store } from '@AppState/redux-store'

let GAME_DATA_API_CONFIG: GameDataConfig
let RAPID_GAMES_API: RapidGamesApi

const getApi = () => {
  if (!RAPID_GAMES_API) {
    throw new Error('Rapid Games API has yet to be defined')
  }
  return RAPID_GAMES_API
}

const configureAPI = (config: GameDataConfig) => {
  GAME_DATA_API_CONFIG = config
  const apiConfig = new Configuration({
    basePath: config.basePath,
    apiKey: config.apiKey,
  })
  RAPID_GAMES_API = new RapidGamesApi(apiConfig)
}

export const mapUnscoredDrawResponseToExtraDrawingAddOn = (
  details: RapidDrawingResponse,
): ExtraDrawsAddOnResult | undefined => {
  const configuredExtraDrawAddOn = getKenoConfigurationForDrawNumber(
    store.getState().gameConfig.gameRules,
    details.result.drawingNumber,
  )?.supportedAddOns.extraDrawAddon
  if (!configuredExtraDrawAddOn) {
    return undefined
  }

  return {
    name: configuredExtraDrawAddOn.name,
    results: Utils.getWinningNumbersFromResult(
      configuredExtraDrawAddOn.resultLocation,
      details.result,
    ).map((res) => {
      return {
        result: res,
        totalWinningsSoFar: 0,
        ticketWinningsSoFar: 0,
      }
    }),
  }
}

export const mapDrawResponseToMultiplierAddOn = (
  details: RapidDrawingResponse,
): MultiplierAddOnResult | undefined => {
  const configuredMultiplierAddon = getKenoConfigurationForDrawNumber(
    store.getState().gameConfig.gameRules,
    details.result.drawingNumber,
  )?.supportedAddOns.multiplierAddon
  if (!configuredMultiplierAddon) {
    return undefined
  }

  return {
    name: configuredMultiplierAddon.name,
    value: Utils.getWinningNumbersFromResult(
      configuredMultiplierAddon.resultLocation,
      details.result,
    )[0],
    possibleResults: configuredMultiplierAddon.availableOptions,
  }
}

export const mapDrawResponseToMiniGameAddOn = (
  details: RapidDrawingResponse,
): MiniGameAddOnResult | undefined => {
  const configuredMiniGameAddon = getKenoConfigurationForDrawNumber(
    store.getState().gameConfig.gameRules,
    details.result.drawingNumber,
  )?.supportedAddOns.miniGameAddon

  if (!configuredMiniGameAddon) {
    return undefined
  }

  return {
    name: configuredMiniGameAddon.name,
    results: Utils.getWinningNumbersFromResult(
      KenoResultLocation.PRIMARY_WINNING_NUMBERS,
      details.result,
    ),
  }
}

const mapRapidDrawingDetailsToGameResult = (details: RapidDrawingResponse): UnscoredDrawResult => {
  return {
    drawingNumber: details.result.drawingNumber,
    drawingDate: details.result.drawingDate.toISOString(),
    winningNumbers: details.result.primary?.winningNumbers ?? [],
    multiplierAddOnResult: mapDrawResponseToMultiplierAddOn(details),
    extraDrawAddOnResult: mapUnscoredDrawResponseToExtraDrawingAddOn(details),
    miniGameAddOnResult: mapDrawResponseToMiniGameAddOn(details),
    jackpot: details.jackpot.jackpot?.jackpotAmountInCents ?? null,
  }
}

const fetchResultsByDraw = async (drawNumber: number | undefined) => {
  const api = getApi()

  const params: GetRapidGameDrawingDetailsRequest = {
    rapidGameId: GAME_DATA_API_CONFIG.rapidGameId,
    xClientID: GAME_DATA_API_CONFIG.clientId,
    drawingNumber: drawNumber,
  }

  const result = await api.getRapidGameDrawingDetails(params)
  return mapRapidDrawingDetailsToGameResult(result)
}

const fetchLatestDrawNumber = async () => {
  const api = getApi()

  const params: GetRapidGameDrawingDetailsRequest = {
    rapidGameId: GAME_DATA_API_CONFIG.rapidGameId,
    xClientID: GAME_DATA_API_CONFIG.clientId,
  }

  const result = await api.getRapidGameDrawingDetails(params)
  return result.result.drawingNumber
}

export const RapidGameDataServiceAPI = {
  configureAPI,
  fetchLatestDrawNumber,
  fetchResultsByDraw,
}
